import {
  BanIcon,
  CheckCircleIcon,
  DotsVerticalIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/outline";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import SignalCardBindings from "./SignalCardBindings";
import SignalCardStats from "./SignalCardStats";

import { convertActionsToContextMenuItems } from "@/components/ui/Cards/Card/utils";
import { ContextMenu, ContextMenuItems } from "@/components/ui/ContextMenu";
import { Action, DropdownMenu, DropdownMenuVariant } from "@/components/ui/DropdownMenu";
import { Signal, SignalStats } from "@/queries/signals";

interface SignalCardProps {
  signal: Signal;
  workspaceId: number;
  stats?: SignalStats;
  isStatsLoading?: boolean;
  onRename: (signal: Signal) => void;
  onDelete: (signal: Signal) => void;
  onToggleEnabled: (signal: Signal) => void;
  isTogglingEnabled?: boolean;
}

const SignalCard = ({
  signal,
  workspaceId,
  stats,
  isStatsLoading = false,
  onRename,
  onDelete,
  onToggleEnabled,
  isTogglingEnabled = false,
}: SignalCardProps) => {
  const navigate = useNavigate();

  const menuItems: Action<Signal>[] = useMemo(
    () => [
      {
        id: "rename",
        label: "Rename",
        icon: PencilIcon,
        variant: DropdownMenuVariant.PRIMARY,
        onClick: () => onRename(signal),
      },
      {
        id: "toggle-enabled",
        label: signal.enabled ? "Disable" : "Enable",
        icon: signal.enabled ? BanIcon : CheckCircleIcon,
        variant: DropdownMenuVariant.PRIMARY,
        getDisabled: () => isTogglingEnabled,
        onClick: () => onToggleEnabled(signal),
      },
      {
        id: "delete",
        label: "Delete",
        icon: TrashIcon,
        variant: DropdownMenuVariant.DESTRUCTIVE,
        onClick: () => onDelete(signal),
      },
    ],
    [isTogglingEnabled, onDelete, onRename, onToggleEnabled, signal],
  );

  const contextMenuItems = useMemo(
    () => convertActionsToContextMenuItems(menuItems) as ContextMenuItems<Signal>[],
    [menuItems],
  );

  return (
    <ContextMenu contextMenuItems={contextMenuItems} data={signal} modal={false} className="h-full">
      <div
        role="button"
        tabIndex={0}
        className={`flex h-full w-full flex-col px-4 py-4 text-left transition-colors ${
          signal.enabled
            ? "cursor-pointer border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
            : "cursor-pointer border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100"
        } rounded-lg border`}
        onClick={() => navigate(`/workspace/${workspaceId}/signals/${signal.id}/edit`)}
        onKeyDown={event => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            navigate(`/workspace/${workspaceId}/signals/${signal.id}/edit`);
          }
        }}
      >
        <div className="flex h-full flex-1 flex-col gap-2">
          <div className="flex min-w-0 shrink-0 items-center gap-2">
            <h3
              className={`min-w-0 truncate text-base font-medium ${
                signal.enabled ? "text-gray-900" : "text-gray-600"
              }`}
            >
              {signal.name}
            </h3>
            <SignalCardStats stats={stats} isLoading={isStatsLoading} />
            <div className="ml-auto shrink-0">
              <DropdownMenu
                menuItems={menuItems}
                item={signal}
                icon={<DotsVerticalIcon className="h-5 w-5" />}
                triggerClassName="rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              />
            </div>
          </div>
          <p
            className={`line-clamp-2 min-h-10 flex-1 text-sm ${
              signal.enabled ? "text-gray-600" : "text-gray-500"
            }`}
          >
            {signal.prompt}
          </p>
          <div className="mt-auto min-h-[1.625rem] shrink-0">
            <SignalCardBindings bindings={signal.bindings} />
          </div>
        </div>
      </div>
    </ContextMenu>
  );
};

export default SignalCard;
